import { useApi } from "@platform/api";
import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";

import {
  ScenarioEdit,
  ScenarioEditType,
  ScenarioLOC,
} from "../pages/BBE/types";
import {
  SCENARIO_ACTION_DRAWER_TYPE,
  ScenarioActionDrawerType,
} from "../pages/BBE/constants";
import { formatDateToStartOfDay } from "../utils/common";
import { AddNewBusinessFormHandle } from "../pages/BBE/components/ScenarioActionDrawer/AddNewBusinessForm/AddNewBusinessForm";
import { buildScenarioEditBody } from "../pages/BBE/utils";
import { ScenarioDetailsTableHandle } from "../pages/BBE/components/ScenarioDetailsTable";

import useToastNotificationService from "./useToastNotificationService";

type UseScenarioDetailsActionsResults = {
  editScenario: UseMutationResult<
    { edits: ScenarioEdit[]; newLocs: ScenarioLOC[] },
    Error
  >;
  removeChangeScenario: UseMutationResult;
  selectedRows: ScenarioLOC[];
  setSelectedRows: React.Dispatch<React.SetStateAction<ScenarioLOC[]>>;
  disabledRemoveChangeBtn: boolean;
  onClickScenarioActionConfirm: (
    effectiveDate: Date | null,
    onSuccessCallback: () => void,
  ) => void;
};

type UseScenarioDetailsActionsParams = {
  baselineId?: string;
  scenarioId: string;
  drawerType: ScenarioActionDrawerType | null;
  formRef: React.RefObject<AddNewBusinessFormHandle>;
  getSnackbarMessage: (
    type: ScenarioActionDrawerType,
    count?: number,
    nullifiedCount?: number,
  ) => string;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCarrier: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    } | null>
  >;
  tableRef: React.RefObject<ScenarioDetailsTableHandle>;
  carrier: {
    label: string;
    value: string;
  } | null;
  setNullifiedModalLines: (lines: string[]) => void;
};

export function useScenarioDetailsActions({
  baselineId,
  scenarioId,
  drawerType,
  formRef,
  getSnackbarMessage,
  setSnackbarMessage,
  setSnackbarOpen,
  setCarrier,
  tableRef,
  carrier,
  setNullifiedModalLines,
}: UseScenarioDetailsActionsParams): UseScenarioDetailsActionsResults {
  const axios = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  const [selectedRows, setSelectedRows] = useState<ScenarioLOC[]>([]);

  const editScenario = useMutation<
    { edits: ScenarioEdit[]; newLocs: ScenarioLOC[] },
    Error,
    ScenarioEdit[]
  >({
    mutationFn: (body) =>
      axios
        .post<{
          edits: ScenarioEdit[];
          newLocs: ScenarioLOC[];
        }>(`/bbe/v1/scenarios/${scenarioId}/edits`, { edits: body })
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bbe", "scenarioDetails", scenarioId],
      });
      if (baselineId) {
        queryClient.invalidateQueries({
          queryKey: ["bbe", "scenariosSummary", baselineId],
        });
      }
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to make edits.",
      });
    },
  });

  const removeChangeScenario = useMutation({
    mutationFn: (editId) =>
      axios.delete(`/bbe/v1/scenarios/${scenarioId}/edits/${editId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bbe", "scenarioDetails", scenarioId],
      });
      if (baselineId) {
        queryClient.invalidateQueries({
          queryKey: ["bbe", "scenariosSummary", baselineId],
        });
      }
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Failed to remove change.",
      });
    },
  });

  const onClickScenarioActionConfirm = useCallback(
    (effectiveDate: Date | null, onSuccessCallback: () => void) => {
      if (!drawerType) return;

      let body: ScenarioEdit[] | undefined;
      let alreadyTerminatedCount = 0;
      let alreadyOnCarrierCount = 0;

      if (drawerType === SCENARIO_ACTION_DRAWER_TYPE.AddNew) {
        const formValues = formRef.current?.getFormValues();
        if (!formValues) return;
        const { step1, step2 } = formValues;
        const effectiveDateStr = new Date().toISOString();
        const originalPlanSaleDate = formatDateToStartOfDay(
          step2.originalSaleDate,
        );
        const clientName = step1.groupId?.brokerName ?? step1.groupName ?? "";

        const groupNumber = step1.groupId?.brokerSk ?? null;
        const saleType = step1.businessType === "BOR" ? "BOR" : "New Sale";

        const newLoc = {
          carrierId: step2.carrier.value,
          groupName: clientName,
          groupNumber,
          policyType: step2.policyType.label,
          planType: step2.planType.name,
          contractType: step2.fundingType.label,
          saleType,
          participants: Number(step2.participants),
          brokerSplit: Number(step2.brokerSplit),
          originalPlanSaleDate,
          averageMonthlyPremiumOverride: Number(step2.averageMonthlyPremium),
          premiumEligibleDate:
            step1.businessType === "BOR"
              ? formatDateToStartOfDay(step2.premiumEligibleDate)
              : null,
        };

        body = [
          {
            editType: ScenarioEditType.AddNew,
            effectiveDate: effectiveDateStr,
            newLoc,
          },
        ] as unknown as ScenarioEdit[];
      } else if (effectiveDate) {
        const result = buildScenarioEditBody({
          drawerType,
          selectedRows,
          effectiveDate,
          destinationCarrierId: carrier?.value,
        });
        body = result.body as unknown as ScenarioEdit[];
        alreadyTerminatedCount = result.alreadyTerminatedCount;
        alreadyOnCarrierCount = result.alreadyOnCarrierCount;
      }

      const handleSuccess = (): void => {
        const actionCount = body?.length ?? 0;
        tableRef.current?.clearSelection();
        setCarrier(null);

        const message = getSnackbarMessage(drawerType, actionCount);
        setSnackbarMessage(message);
        setSnackbarOpen(true);

        const nullifiedLines: string[] = [];
        if (alreadyTerminatedCount > 0) {
          nullifiedLines.push(
            `${alreadyTerminatedCount} coverage(s) were already terminated and were not affected.`,
          );
        }
        if (alreadyOnCarrierCount > 0) {
          nullifiedLines.push(
            `${alreadyOnCarrierCount} coverage(s) were already on the selected carrier and were not migrated.`,
          );
        }
        setNullifiedModalLines(nullifiedLines);

        onSuccessCallback();
      };

      if (!body || body.length === 0) {
        handleSuccess();
        return;
      }

      editScenario.mutate(body, { onSuccess: handleSuccess });
    },
    [
      drawerType,
      editScenario,
      formRef,
      selectedRows,
      carrier?.value,
      getSnackbarMessage,
      setSnackbarMessage,
      tableRef,
      setCarrier,
      setSnackbarOpen,
      setNullifiedModalLines,
    ],
  );

  const disabledRemoveChangeBtn =
    selectedRows.filter((row) => row.editId).length === 0;

  return {
    editScenario,
    removeChangeScenario,
    selectedRows,
    setSelectedRows,
    disabledRemoveChangeBtn,
    onClickScenarioActionConfirm,
  };
}
